import { ACCESS_KEYS } from '@/config/entitlementKeys';
import i18n from '@/i18n';
import { mdiCodeBracesBox, mdiToolboxOutline } from '@mdi/js';

export default [
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('tools_collection.title'),
      icon: mdiToolboxOutline,
      requiresAuth: true,
      demo: true,
      hideNewsletter: true,
      hideFooter: true,
      breadcrumbs: [{ label: i18n.global.t('tools_collection.title'), to: '/tools' }],
    },
    path: '/tools',
    name: 'tools.collection',
    component: () => import('@/pages/tools-collection/ToolsCollection.vue'),
  },
  {
    // Graceful locked state for a tool the user isn't entitled to. Reached via
    // the entitlement guard's redirect — deliberately NOT entitlement-gated
    // itself (it IS the upgrade surface). `?key=` carries the missing
    // entitlement so the CTA can deep-link to the right plan.
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('tools_collection.title'),
      requiresAuth: true,
      hideNewsletter: true,
      breadcrumbs: [{ label: i18n.global.t('menu_tools'), to: '/tools' }],
    },
    path: '/tools/locked/:toolKey',
    name: 'tool.locked',
    component: () => import('@/pages/tools/ToolLockedPage.vue'),
    props: true,
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.control_state_prediction'),
      requiresAuth: true,
      demo: true,
      requiresEntitlement: ACCESS_KEYS.TOOL_CONTROL_STATE,
      favoritable: { kind: 'tool', id: 'control-state-prediction' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('page_titles.control_state_prediction'),
          to: '/tools/control-state',
        },
      ],
    },
    path: '/tools/control-state',
    name: 'control-state',
    component: () => import('../../features/regulation-state/pages/RegulationStateEntryPage.vue'),
  },
  // Generic "pick a benchmark type" landing — surfaces the three benchmark
  // cards. Honors `?benchmark=imbalance|bidding|forward` to skip the picker.
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('tools.benchmarks.wizard.pick_type_title'),
      requiresAuth: true,
      demo: true,
      requiresEntitlement: [
        ACCESS_KEYS.TOOL_BIDDING_STRATEGY,
        ACCESS_KEYS.TOOL_FORWARD_BENCHMARK,
        ACCESS_KEYS.TOOL_IMBALANCE_BENCHMARK,
      ],
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('tools.benchmarks.wizard.new_run'),
          to: '/tools/benchmarks/new',
        },
      ],
    },
    path: '/tools/benchmarks/new',
    name: 'benchmarks-new',
    component: () => import('../../features/benchmarks/pages/BenchmarkTypeChooserPage.vue'),
  },
  // --- Bidding Strategy benchmark: list / new / run ---
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.bidding_strategy'),
      requiresAuth: true,
      requiresEntitlement: ACCESS_KEYS.TOOL_BIDDING_STRATEGY,
      favoritable: { kind: 'tool', id: 'bidding-strategy' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        { label: i18n.global.t('page_titles.bidding_strategy'), to: '/tools/bidding-strategy' },
      ],
    },
    path: '/tools/bidding-strategy',
    name: 'bidding-strategy',
    component: () => import('../../features/benchmarks/pages/BenchmarkListPage.vue'),
    props: { benchmarkKey: 'bidding' },
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.bidding_strategy'),
      requiresAuth: true,
      demo: true,
      requiresEntitlement: ACCESS_KEYS.TOOL_BIDDING_STRATEGY,
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        { label: i18n.global.t('page_titles.bidding_strategy'), to: '/tools/bidding-strategy' },
        {
          label: i18n.global.t('tools.benchmarks.wizard.new_run'),
          to: '/tools/bidding-strategy/new',
        },
      ],
    },
    path: '/tools/bidding-strategy/new',
    name: 'bidding-strategy-new',
    component: () => import('../../features/benchmarks/pages/BenchmarkWizardPage.vue'),
    props: { benchmarkKey: 'bidding', mode: 'new' },
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.bidding_strategy'),
      requiresAuth: true,
      requiresEntitlement: ACCESS_KEYS.TOOL_BIDDING_STRATEGY,
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        { label: i18n.global.t('page_titles.bidding_strategy'), to: '/tools/bidding-strategy' },
      ],
    },
    path: '/tools/bidding-strategy/runs/:runId',
    name: 'bidding-strategy-run',
    component: () => import('../../features/benchmarks/pages/BenchmarkWizardPage.vue'),
    props: (route) => ({ benchmarkKey: 'bidding', mode: 'run', runId: route.params.runId }),
  },

  // --- Hedging Benchmark: list / new / run ---
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('tool_labels.hedging-benchmark'),
      requiresAuth: true,
      requiresFeatureFlag: 'hedging-benchmark',
      requiresEntitlement: ACCESS_KEYS.TOOL_HEDGING_BENCHMARK,
      favoritable: { kind: 'tool', id: 'hedging-benchmark' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('tool_labels.hedging-benchmark'),
          to: '/tools/hedging-benchmark',
        },
      ],
    },
    path: '/tools/hedging-benchmark',
    name: 'hedging-benchmark',
    component: () => import('../../features/benchmarks/pages/BenchmarkListPage.vue'),
    props: { benchmarkKey: 'hedging' },
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('tool_labels.hedging-benchmark'),
      requiresAuth: true,
      requiresFeatureFlag: 'hedging-benchmark',
      requiresEntitlement: ACCESS_KEYS.TOOL_HEDGING_BENCHMARK,
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('tool_labels.hedging-benchmark'),
          to: '/tools/hedging-benchmark',
        },
        {
          label: i18n.global.t('tools.benchmarks.wizard.new_run'),
          to: '/tools/hedging-benchmark/new',
        },
      ],
    },
    path: '/tools/hedging-benchmark/new',
    name: 'hedging-benchmark-new',
    component: () => import('../../features/benchmarks/pages/BenchmarkWizardPage.vue'),
    props: { benchmarkKey: 'hedging', mode: 'new' },
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('tool_labels.hedging-benchmark'),
      requiresAuth: true,
      requiresFeatureFlag: 'hedging-benchmark',
      requiresEntitlement: ACCESS_KEYS.TOOL_HEDGING_BENCHMARK,
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('tool_labels.hedging-benchmark'),
          to: '/tools/hedging-benchmark',
        },
      ],
    },
    path: '/tools/hedging-benchmark/runs/:runId',
    name: 'hedging-benchmark-run',
    component: () => import('../../features/benchmarks/pages/BenchmarkWizardPage.vue'),
    props: (route) => ({
      benchmarkKey: 'hedging',
      mode: 'run',
      runId: route.params.runId,
    }),
  },

  // --- Forward Benchmark benchmark: list / new / run ---
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.forward_benchmark'),
      requiresAuth: true,
      requiresFeatureFlag: 'forward-benchmark',
      requiresEntitlement: ACCESS_KEYS.TOOL_FORWARD_BENCHMARK,
      favoritable: { kind: 'tool', id: 'forward-benchmark' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        { label: i18n.global.t('page_titles.forward_benchmark'), to: '/tools/forward-benchmark' },
      ],
    },
    path: '/tools/forward-benchmark',
    name: 'forward-benchmark',
    component: () => import('../../features/benchmarks/pages/BenchmarkListPage.vue'),
    props: { benchmarkKey: 'forward' },
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.forward_benchmark'),
      requiresAuth: true,
      demo: true,
      requiresFeatureFlag: 'forward-benchmark',
      requiresEntitlement: ACCESS_KEYS.TOOL_FORWARD_BENCHMARK,
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        { label: i18n.global.t('page_titles.forward_benchmark'), to: '/tools/forward-benchmark' },
        {
          label: i18n.global.t('tools.benchmarks.wizard.new_run'),
          to: '/tools/forward-benchmark/new',
        },
      ],
    },
    path: '/tools/forward-benchmark/new',
    name: 'forward-benchmark-new',
    component: () => import('../../features/benchmarks/pages/BenchmarkWizardPage.vue'),
    props: { benchmarkKey: 'forward', mode: 'new' },
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.forward_benchmark'),
      requiresAuth: true,
      requiresFeatureFlag: 'forward-benchmark',
      requiresEntitlement: ACCESS_KEYS.TOOL_FORWARD_BENCHMARK,
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        { label: i18n.global.t('page_titles.forward_benchmark'), to: '/tools/forward-benchmark' },
      ],
    },
    path: '/tools/forward-benchmark/groups/:groupId',
    name: 'forward-benchmark-group',
    component: () => import('../../features/benchmarks/pages/BenchmarkWizardPage.vue'),
    props: (route) => ({ benchmarkKey: 'forward', mode: 'group', groupId: route.params.groupId }),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.forward_benchmark'),
      requiresAuth: true,
      requiresFeatureFlag: 'forward-benchmark',
      requiresEntitlement: ACCESS_KEYS.TOOL_FORWARD_BENCHMARK,
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        { label: i18n.global.t('page_titles.forward_benchmark'), to: '/tools/forward-benchmark' },
      ],
    },
    path: '/tools/forward-benchmark/runs/:runId',
    name: 'forward-benchmark-run',
    component: () => import('../../features/benchmarks/pages/BenchmarkWizardPage.vue'),
    props: (route) => ({ benchmarkKey: 'forward', mode: 'run', runId: route.params.runId }),
  },

  // --- Imbalance Benchmark: list / new / run ---
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.imbalance_benchmark'),
      requiresAuth: true,
      requiresEntitlement: ACCESS_KEYS.TOOL_IMBALANCE_BENCHMARK,
      favoritable: { kind: 'tool', id: 'imbalance-benchmark' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('page_titles.imbalance_benchmark'),
          to: '/tools/imbalance-benchmark',
        },
      ],
    },
    path: '/tools/imbalance-benchmark',
    name: 'imbalance-benchmark',
    component: () => import('../../features/benchmarks/pages/BenchmarkListPage.vue'),
    props: { benchmarkKey: 'imbalance' },
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.imbalance_benchmark'),
      requiresAuth: true,
      demo: true,
      requiresEntitlement: ACCESS_KEYS.TOOL_IMBALANCE_BENCHMARK,
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('page_titles.imbalance_benchmark'),
          to: '/tools/imbalance-benchmark',
        },
        {
          label: i18n.global.t('tools.benchmarks.wizard.new_run'),
          to: '/tools/imbalance-benchmark/new',
        },
      ],
    },
    path: '/tools/imbalance-benchmark/new',
    name: 'imbalance-benchmark-new',
    component: () => import('../../features/benchmarks/pages/BenchmarkWizardPage.vue'),
    props: { benchmarkKey: 'imbalance', mode: 'new' },
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.imbalance_benchmark'),
      requiresAuth: true,
      requiresEntitlement: ACCESS_KEYS.TOOL_IMBALANCE_BENCHMARK,
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('page_titles.imbalance_benchmark'),
          to: '/tools/imbalance-benchmark',
        },
      ],
    },
    path: '/tools/imbalance-benchmark/runs/:runId',
    name: 'imbalance-benchmark-run',
    component: () => import('../../features/benchmarks/pages/BenchmarkWizardPage.vue'),
    props: (route) => ({ benchmarkKey: 'imbalance', mode: 'run', runId: route.params.runId }),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.imbalance_benchmark'),
      requiresAuth: true,
      requiresEntitlement: ACCESS_KEYS.TOOL_IMBALANCE_BENCHMARK,
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('page_titles.imbalance_benchmark'),
          to: '/tools/imbalance-benchmark',
        },
      ],
    },
    path: '/tools/imbalance-benchmark/groups/:groupId',
    name: 'imbalance-benchmark-group',
    component: () => import('../../features/benchmarks/pages/BenchmarkWizardPage.vue'),
    props: (route) => ({ benchmarkKey: 'imbalance', mode: 'group', groupId: route.params.groupId }),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.day_ahead_prediction'),
      requiresAuth: true,
      demo: true,
      requiresEntitlement: ACCESS_KEYS.DATASET_DAY_AHEAD_PREDICTIONS,
      favoritable: { kind: 'tool', id: 'day-ahead-prediction' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('page_titles.day_ahead_prediction'),
          to: '/tools/day-ahead-forecast',
        },
      ],
    },
    path: '/tools/day-ahead-forecast',
    name: 'day-ahead-forecast',
    component: () => import('../../pages/tools/forecasts/DashboardDayAheadPrediction.vue'),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.spike_probability'),
      requiresAuth: true,
      demo: true,
      requiresFeatureFlag: 'spike-probability',
      requiresEntitlement: ACCESS_KEYS.DATASET_SPIKE_PROBABILITY_PREDICTIONS,
      favoritable: { kind: 'tool', id: 'spike-probability' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('page_titles.spike_probability'),
          to: '/tools/spike-probability',
        },
      ],
    },
    path: '/tools/spike-probability',
    name: 'spike-probability',
    component: () => import('../../pages/tools/forecasts/DashboardSpikeProbability.vue'),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.generation_map'),
      requiresAuth: true,
      requiresEntitlement: ACCESS_KEYS.TOOL_GENERATION_MAP,
      favoritable: { kind: 'tool', id: 'generation-map' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('page_titles.generation_map'),
          to: '/tools/generation-map',
        },
      ],
    },
    path: '/tools/generation-map',
    name: 'generation-map',
    component: () => import('../../pages/tools/forecasts/GenerationMapDashboard.vue'),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.grid_map'),
      requiresAuth: true,
      // No entitlement of its own: the live overlays behind this page are gated
      // on the generation map, so that is what the page needs too.
      requiresEntitlement: ACCESS_KEYS.TOOL_GENERATION_MAP,
      favoritable: { kind: 'tool', id: 'grid-map' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('page_titles.grid_map'),
          to: '/tools/grid-map',
        },
      ],
    },
    path: '/tools/grid-map',
    name: 'grid-map',
    component: () => import('../../features/grid-map/pages/GridMapDashboard.vue'),
  },
  // Earlier names of the same map (Netwerkstatus, May 2026); kept for bookmarks.
  {
    path: '/tools/grid-status',
    redirect: '/tools/grid-map',
  },
  {
    path: '/tools/grid-topology',
    redirect: '/tools/grid-map',
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.grid_state'),
      requiresAuth: true,
      requiresEntitlement: ACCESS_KEYS.TOOL_GRID_STATE,
      favoritable: { kind: 'tool', id: 'grid-state' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('page_titles.grid_state'),
          to: '/tools/grid-state',
        },
      ],
    },
    path: '/tools/grid-state',
    name: 'grid-state',
    component: () => import('../../pages/tools/forecasts/GridStateForecastDashboard.vue'),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.capacity_fee_forecaster'),
      requiresAuth: true,
      demo: true,
      requiresFeatureFlag: 'capacity-fee-forecaster',
      requiresEntitlement: ACCESS_KEYS.DATASET_CAPACITY_FEE_PREDICTIONS,
      favoritable: { kind: 'tool', id: 'capacity-fee-forecaster' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('page_titles.capacity_fee_forecaster'),
          to: '/tools/capacity-fee-forecaster',
        },
      ],
    },
    path: '/tools/capacity-fee-forecaster',
    name: 'capacity-fee-forecaster',
    component: () => import('../../pages/tools/forecasts/DashboardCapacityFeeForecaster.vue'),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.power_market_simulator'),
      requiresAuth: true,
      requiresEntitlement: ACCESS_KEYS.TOOL_POWER_MARKET_SIMULATOR,
      requiresFeatureFlag: 'power-market-simulator',
      favoritable: { kind: 'tool', id: 'power-market-simulator' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('page_titles.power_market_simulator'),
          to: '/tools/power-market-simulator',
        },
      ],
    },
    path: '/tools/power-market-simulator',
    name: 'power-market-simulator',
    component: () =>
      import('../../features/power-market-simulator/pages/DashboardPowerMarketSimulator.vue'),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.orderbook'),
      requiresAuth: true,
      requiresEntitlement: ACCESS_KEYS.TOOL_ORDERBOOK,
      favoritable: { kind: 'tool', id: 'orderbook' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        { label: i18n.global.t('page_titles.orderbook'), to: '/tools/orderbook' },
      ],
    },
    path: '/tools/orderbook',
    name: 'orderbook',
    component: () => import('../../features/orderbook/pages/DashboardOrderBook.vue'),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.water-risk'),
      requiresAuth: true,
      requiresFeatureFlag: 'water-risk',
      requiresEntitlement: ACCESS_KEYS.TOOL_WATER_RISK,
      analytics: { toolId: 'water-risk', presence: true },
      favoritable: { kind: 'tool', id: 'water-risk' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        { label: i18n.global.t('page_titles.water-risk'), to: '/tools/water-risk' },
      ],
    },
    path: '/tools/water-risk',
    name: 'water-risk',
    component: () => import('../../features/water-risk/pages/DashboardWaterRisk.vue'),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.distribution-interruptions'),
      requiresAuth: true,
      requiresFeatureFlag: 'distribution-interruptions',
      analytics: { toolId: 'distribution-interruptions', presence: true },
      favoritable: { kind: 'tool', id: 'distribution-interruptions' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        {
          label: i18n.global.t('page_titles.distribution-interruptions'),
          to: '/tools/distribution-interruptions',
        },
      ],
    },
    path: '/tools/distribution-interruptions',
    name: 'distribution-interruptions',
    component: () =>
      import(
        '../../features/distribution-interruptions/pages/DashboardDistributionInterruptions.vue'
      ),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.shadow_trading'),
      requiresAuth: true,
      requiresFeatureFlag: 'shadow-trading',
      requiresEntitlement: ACCESS_KEYS.TOOL_SHADOW_TRADING,
      hideToolCategoryBreadcrumb: true,
      favoritable: { kind: 'tool', id: 'shadow-trading' },
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        { label: i18n.global.t('page_titles.shadow_trading'), to: '/tools/shadow-trading' },
      ],
    },
    path: '/tools/shadow-trading',
    name: 'shadow-trading',
    component: () =>
      import('../../features/shadow-trading/pages/ShadowTradingSimulationsIndexPage.vue'),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.shadow_trading'),
      requiresAuth: true,
      requiresFeatureFlag: 'shadow-trading',
      requiresEntitlement: ACCESS_KEYS.TOOL_SHADOW_TRADING,
      hideToolCategoryBreadcrumb: true,
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        { label: i18n.global.t('page_titles.shadow_trading'), to: '/tools/shadow-trading' },
        {
          label: i18n.global.t('tools.shadow_trading.lifecycle.live'),
          to: '/tools/shadow-trading/live',
        },
      ],
    },
    path: '/tools/shadow-trading/live',
    name: 'shadow-trading-live',
    component: () => import('../../features/shadow-trading/pages/ShadowTradingDashboardPage.vue'),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.shadow_trading'),
      requiresAuth: true,
      demo: true,
      requiresFeatureFlag: 'shadow-trading',
      requiresEntitlement: ACCESS_KEYS.TOOL_SHADOW_TRADING,
      hideToolCategoryBreadcrumb: true,
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        { label: i18n.global.t('page_titles.shadow_trading'), to: '/tools/shadow-trading' },
        {
          label: i18n.global.t('tools.shadow_trading.form.dialog_title'),
          to: '/tools/shadow-trading/new',
        },
      ],
    },
    path: '/tools/shadow-trading/new',
    name: 'shadow-trading-new',
    component: () => import('../../features/shadow-trading/pages/ShadowTradingCreateWizard.vue'),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.shadow_trading'),
      requiresAuth: true,
      requiresFeatureFlag: 'shadow-trading',
      requiresEntitlement: ACCESS_KEYS.TOOL_SHADOW_TRADING,
      hideToolCategoryBreadcrumb: true,
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        { label: i18n.global.t('page_titles.shadow_trading'), to: '/tools/shadow-trading' },
        {
          label: i18n.global.t('tools.shadow_trading.strategy_library.market_title'),
          to: '/tools/shadow-trading/strategies',
        },
      ],
    },
    path: '/tools/shadow-trading/strategies',
    name: 'shadow-trading-strategies',
    component: () =>
      import('../../features/shadow-trading/pages/ShadowTradingStrategyLibraryPage.vue'),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.shadow_trading'),
      requiresAuth: true,
      requiresFeatureFlag: 'shadow-trading',
      requiresEntitlement: ACCESS_KEYS.TOOL_SHADOW_TRADING,
      hideToolCategoryBreadcrumb: true,
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        { label: i18n.global.t('page_titles.shadow_trading'), to: '/tools/shadow-trading' },
        {
          label: i18n.global.t('tools.shadow_trading.strategy_library.day_title'),
          to: '/tools/shadow-trading/day-strategies',
        },
      ],
    },
    path: '/tools/shadow-trading/day-strategies',
    name: 'shadow-trading-day-strategies',
    component: () =>
      import('../../features/shadow-trading/pages/ShadowTradingDayStrategiesPage.vue'),
  },
  {
    meta: {
      cauldron: 'executive',
      title: i18n.global.t('page_titles.shadow_trading'),
      requiresAuth: true,
      requiresFeatureFlag: 'shadow-trading',
      requiresEntitlement: ACCESS_KEYS.TOOL_SHADOW_TRADING,
      hideToolCategoryBreadcrumb: true,
      breadcrumbs: [
        { label: i18n.global.t('menu_tools'), to: '/tools' },
        { label: i18n.global.t('page_titles.shadow_trading'), to: '/tools/shadow-trading' },
        {
          label: i18n.global.t('time_horizon.historical'),
          to: '/tools/shadow-trading/history',
        },
      ],
    },
    path: '/tools/shadow-trading/history',
    name: 'shadow-trading-history',
    component: () =>
      import('../../features/shadow-trading/pages/ShadowTradingSimulationDashboardPage.vue'),
  },
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('api_docs.v2.title'),
      icon: mdiCodeBracesBox,
      requiresAuth: false,
      hideNewsletter: true,
      favoritable: {
        kind: 'page',
        id: 'page:api-docs',
        label: i18n.global.t('menu_for_developers'),
      },
      breadcrumbs: [{ label: i18n.global.t('menu_for_developers'), to: '/api-docs' }],
    },
    path: '/api-docs',
    name: 'api-docs-landing',
    component: () => import('../../features/api-documentation/docs/DocsPagePublic.vue'),
  },
];
